/**
 * Cross-package report DTOs — shared by the API (which produces them) and the
 * typed client (which receives them) so the wire contract has a single source
 * of truth and can't drift between the two.
 */

/** Grand totals across ALL matching rows of a report (the whole filtered set,
 *  not the current page) — drives the timesheet report's footer/summary line.
 *  Each is a numeric string as summed in SQL, or `null` when nothing
 *  contributes (no matching rows, or none billable for `amount`). */
export interface ReportTotals {
  hours: string | null;
  amount: string | null;
}

/** Columns the timesheet report can be sorted on (server-side, G13). This
 *  const is the single source of truth: the API builds its zod enum from it,
 *  the derived `ReportSortField` type flows to the client + web, so the sort
 *  vocabulary can't drift across the three. */
export const REPORT_SORT_FIELDS = [
  'date',
  'employeeName',
  'customerName',
  'status',
  'hours',
  'dateSubmitted',
] as const;

export type ReportSortField = (typeof REPORT_SORT_FIELDS)[number];

/** A report column's stable key + human header, as served by the API's
 *  `/columns` endpoint. `type` is the cell's native kind — the preview table
 *  right-aligns `'number'` columns and renders `'date'` columns through the
 *  shared date formatter, mirroring how the xlsx/csv export types those cells. */
export interface ReportColumnMeta {
  key: string;
  header: string;
  type: 'text' | 'number' | 'date';
}

/** One report row: a `{ columnKey: cellString }` map. The server formats every
 *  cell, so the client renders strings directly (no per-column logic). */
export type ReportRow = Record<string, string>;

/**
 * Reports that ship WITH THE PRODUCT rather than being saved by somebody — the
 * stable keys a label or a star can point at instead of a saved report's id.
 *
 * Keys, not routes: the key is stored in `report_label_items.builtin_key`, and
 * a route is free to move without rewriting rows. The web app maps each key to
 * its title and path; the API only checks that a key is one of these.
 */
export const BUILT_IN_REPORT_KEYS = ['timesheets'] as const;

export type BuiltInReportKey = (typeof BUILT_IN_REPORT_KEYS)[number];
